import './styles.css';
import { GameBoard } from './gameBoard.js';
import { apiClient, ApiError } from './api.js';
import { SignalRService, GameUpdateCallbacks } from './signalr.js';
import { authService } from './auth.js';
import {
  Position,
  Theme,
  GameState,
  UIState,
  MakeMoveDto,
  CreateGameDto,
  BoardStateDto
} from './types.js';

class MigoYugoApp {
  private gameBoard: GameBoard;
  private signalR: SignalRService;
  private gameState: GameState = {
    isLoading: false,
    isAuthenticated: false,
    isMakingMove: false
  };
  private uiState: UIState = {
    theme: 'classic',
    showJoinGameModal: false
  };
  private playbackState = {
    boardHistory: [] as BoardStateDto[],
    moveHistory: [] as { position: Position; moveNumber: number }[],
    currentIndex: -1, // -1 means live view
    isPlayback: false
  };

  // DOM Elements
  private elements = {
    loadingScreen: document.getElementById('loadingScreen')!,
    modalOverlay: document.getElementById('modalOverlay')!,
    joinGameModal: document.getElementById('joinGameModal')!,
    joinGameForm: document.getElementById('joinGameForm')! as HTMLFormElement,
    closeJoinGameModal: document.getElementById('closeJoinGameModal')!,
    cancelJoinGameBtn: document.getElementById('cancelJoinGameBtn')!,
    joinGameError: document.getElementById('joinGameError')!,
    userInfo: document.getElementById('userInfo')!,
    gameBoard: document.getElementById('gameBoard')!,
    player1Info: document.getElementById('player1Info')!,
    player2Info: document.getElementById('player2Info')!,
    player1Turn: document.getElementById('player1Turn')!,
    player2Turn: document.getElementById('player2Turn')!,
    newGameBtn: document.getElementById('newGameBtn')!,
    joinByIdBtn: document.getElementById('joinByIdBtn')!,
    joinThisGameBtn: document.getElementById('joinThisGameBtn')!,
    resignBtn: document.getElementById('resignBtn')!,
    gameInfo: document.getElementById('gameInfo')!,
    gameStats: document.getElementById('gameStats')!,
    gameIdDisplay: document.getElementById('gameIdDisplay')! as HTMLInputElement,
    copyGameIdBtn: document.getElementById('copyGameIdBtn')!,
    moveHistory: document.getElementById('moveHistory')!,
    themeSelect: document.getElementById('themeSelect')! as HTMLSelectElement,
    gameStatus: document.getElementById('gameStatus')!,
    connectionStatus: document.getElementById('connectionStatus')!,
    whiteYugos: document.getElementById('whiteYugos')!,
    blackYugos: document.getElementById('blackYugos')!,
    whiteMigos: document.getElementById('whiteMigos')!,
    blackMigos: document.getElementById('blackMigos')!,
    // Playback controls
    firstMoveBtn: document.getElementById('firstMoveBtn')! as HTMLButtonElement,
    prevMoveBtn: document.getElementById('prevMoveBtn')! as HTMLButtonElement,
    nextMoveBtn: document.getElementById('nextMoveBtn')! as HTMLButtonElement,
    lastMoveBtn: document.getElementById('lastMoveBtn')! as HTMLButtonElement,
    liveBtn: document.getElementById('liveBtn')! as HTMLButtonElement,
    currentMoveText: document.getElementById('currentMoveText')!,
    playbackPosition: document.getElementById('playbackPosition')!
  };

  constructor() {
    this.gameBoard = new GameBoard(this.elements.gameBoard);
    this.signalR = new SignalRService();
    this.initialize();
  }

  private async initialize() {
    this.showLoading(true);
    
    // Set up event listeners
    this.setupEventListeners();
    this.setupSignalRCallbacks();
    
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) {
      this.setTheme(savedTheme);
    }
    
    // Check authentication
    await this.checkAuthentication();
    
    // Always update user info to show login/guest buttons
    this.updateUserInfo();
    this.updateGameControls();
    
    // Connect to SignalR only if authenticated
    if (this.gameState.isAuthenticated) {
      await this.connectSignalR();
    }
    
    // Check if there's a game ID in the URL (authenticated or not)
    const gameIdFromUrl = this.getGameIdFromUrl();
    if (gameIdFromUrl) {
      await this.loadGameFromUrl(gameIdFromUrl);
    }
    
    // Hide loading screen
    this.showLoading(false);
    
    console.log('MigoYugo initialized');
  }

  private setupEventListeners() {
    // Authentication buttons are now added dynamically in updateUserInfo()
    
    // Listen for browser back/forward navigation
    window.addEventListener('popstate', async () => {
      const gameIdFromUrl = this.getGameIdFromUrl();
      if (gameIdFromUrl) {
        await this.loadGameFromUrl(gameIdFromUrl);
      } else {
        // Clear the game if no game ID in URL
        this.gameState.game = undefined;
        this.gameBoard.reset();
        this.updatePlayerInfo();
        this.updateGameControls();
        this.updateGameInfo();
        this.clearMoveHistory();
        this.updateGameStatus('No game loaded');
      }
    });
    
    // Join Game Modal
    this.elements.joinByIdBtn.addEventListener('click', () => this.showJoinGameModal(true));
    this.elements.joinThisGameBtn.addEventListener('click', () => this.joinCurrentGame());
    this.elements.closeJoinGameModal.addEventListener('click', () => this.showJoinGameModal(false));
    this.elements.cancelJoinGameBtn.addEventListener('click', () => this.showJoinGameModal(false));
    this.elements.joinGameForm.addEventListener('submit', (e) => this.handleJoinGame(e));
    
    // Game controls
    this.elements.newGameBtn.addEventListener('click', () => this.startNewGame());
    this.elements.resignBtn.addEventListener('click', () => this.resignGame());
    this.elements.copyGameIdBtn.addEventListener('click', () => this.copyGameId());
    
    // Theme selection
    this.elements.themeSelect.addEventListener('change', (e) => {
      const target = e.target as HTMLSelectElement;
      this.setTheme(target.value as Theme);
    });
    
    // Playback controls
    this.elements.firstMoveBtn.addEventListener('click', () => this.goToMove(0));
    this.elements.prevMoveBtn.addEventListener('click', () => this.goToPreviousMove());
    this.elements.nextMoveBtn.addEventListener('click', () => this.goToNextMove());
    this.elements.lastMoveBtn.addEventListener('click', () => this.goToLastMove());
    this.elements.liveBtn.addEventListener('click', () => this.returnToLive());
    
    // Game board clicks
    this.gameBoard.setCellClickHandler((position) => this.handleCellClick(position));
    
    // Modal overlay clicks (close modal when clicking outside)
    this.elements.modalOverlay.addEventListener('click', (e) => {
      if (e.target === this.elements.modalOverlay) {
        this.showJoinGameModal(false);
      }
    });

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.showJoinGameModal(false);
      }
    });
  }

  private setupSignalRCallbacks() {
    const callbacks: GameUpdateCallbacks = {
      onMoveReceived: (gameId, move, result) => {
        this.handleRemoteMove(gameId, move, result);
      },
      onPlayerJoined: (gameId, player) => {
        this.handlePlayerJoined(gameId, player);
      },
      onPlayerLeft: (gameId, playerId) => {
        this.handlePlayerLeft(gameId, playerId);
      },
      onGameStateChanged: (gameId, game) => {
        this.handleGameStateChanged(gameId, game);
      },
      onConnectionChanged: (connected) => {
        this.handleConnectionChanged(connected);
      }
    };
    
    this.signalR.setCallbacks(callbacks);
  }

  private async connectSignalR() {
    try {
      const token = authService.getToken();
      const connected = await this.signalR.connect(token || undefined);
      this.updateConnectionStatus(connected);
    } catch (error) {
      console.error('Failed to connect to SignalR:', error);
      this.updateConnectionStatus(false);
    }
  }

  private updateConnectionStatus(connected: boolean) {
    const statusIndicator = this.elements.connectionStatus.querySelector('.status-indicator')!;
    const statusLabel = this.elements.connectionStatus.querySelector('.status-label')!;
    
    if (connected) {
      statusIndicator.classList.remove('status-indicator--disconnected');
      statusLabel.textContent = 'Connected';
    } else {
      statusIndicator.classList.add('status-indicator--disconnected');
      statusLabel.textContent = 'Disconnected';
    }
  }

  private async checkAuthentication() {
    try {
      // Initialize Keycloak
      const authenticated = await authService.init();
      
      if (authenticated) {
        this.gameState.isAuthenticated = true;
        this.gameState.currentUser = {
          id: authService.getUserId() || '',
          username: authService.getUsername() || 'User',
          rating: 1000,
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          gamesDrawn: 0
        };
        
        // Set the token in the API client
        const token = authService.getToken();
        if (token) {
          apiClient.setToken(token);
        }
        
        this.updateUserInfo();
        this.updateGameControls();
        this.updateGameControls();
        console.log('User authenticated via Keycloak');
      } else {
        this.gameState.isAuthenticated = false;
        this.gameState.currentUser = undefined;
        console.log('User not authenticated');
      }
    } catch (error) {
      console.error('Authentication check failed:', error);
      this.gameState.isAuthenticated = false;
      this.gameState.currentUser = undefined;
      this.showError('Unable to connect to authentication service. Please check if Keycloak is running.');
    }
  }

  private showLoading(show: boolean) {
    this.gameState.isLoading = show;
    this.elements.loadingScreen.classList.toggle('hidden', !show);
  }


  private showJoinGameModal(show: boolean) {
    this.uiState.showJoinGameModal = show;
    
    if (show) {
      this.elements.joinGameModal.style.display = 'block';
      this.elements.joinGameError.style.display = 'none';
      
      // Clear previous input and focus
      const gameIdInput = this.elements.joinGameForm.querySelector('#gameIdInput') as HTMLInputElement;
      gameIdInput.value = '';
      setTimeout(() => gameIdInput.focus(), 100);
    } else {
      this.elements.joinGameModal.style.display = 'none';
    }
    
    this.elements.modalOverlay.classList.toggle('hidden', !show);
  }

  private async handleJoinGame(e: Event) {
    e.preventDefault();
    
    this.elements.joinGameError.style.display = 'none';
    
    const formData = new FormData(this.elements.joinGameForm);
    const gameId = formData.get('gameId') as string;

    if (!gameId || !gameId.trim()) {
      this.showJoinGameError('Please enter a game ID');
      return;
    }

    if (!this.gameState.isAuthenticated) {
      this.showJoinGameError('Please login first to join a game. Redirecting to login...');
      this.showJoinGameModal(false);
      setTimeout(() => authService.login(), 1500);
      return;
    }

    this.showLoading(true);
    
    try {
      // Join the game (backend will handle validation and adding player)
      const game = await apiClient.joinGame(gameId.trim());
      
      // Load the game
      this.gameState.game = game;
      
      // Update URL with game ID
      this.updateUrlWithGameId(game.id);
      
      if (game.currentBoard) {
        this.gameBoard.updateBoard(game.currentBoard);
        this.gameBoard.setDisabled(false);
        
        // Record initial board state if game has moves
        if (game.recentMoves.length > 0) {
          // We need to reconstruct the board history
          // For now, just record the current state
          this.recordBoardState(game.currentBoard);
        }
      }
      
      this.updatePlayerInfo();
      this.updateGameControls();
      this.updateGameInfo();
      this.clearMoveHistory();
      
      // Add recent moves to history if any
      if (game.recentMoves.length > 0) {
        game.recentMoves
          .sort((a, b) => a.moveNumber - b.moveNumber)
          .forEach(move => {
            const position = { row: move.fromX, col: move.fromY };
            this.addMoveToHistory(position, move.moveNumber);
          });
      }
      
      // Join SignalR group for this game
      await this.signalR.joinGameGroup(game.id);
      
      this.showJoinGameModal(false);
      this.elements.joinGameForm.reset();
      
      // Update status message
      if (game.players.length === 2) {
        const currentPlayer = game.currentBoard?.currentPlayerIndex === 0 ? 'White' : 'Black';
        this.updateGameStatus(`Game ready! ${currentPlayer} to move.`);
      } else {
        this.updateGameStatus(`Joined game! Waiting for another player...`);
      }
      
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          this.showJoinGameError('Game not found. Please check the game ID.');
        } else {
          this.showJoinGameError(`Failed to join game: ${error.message}`);
        }
      } else {
        this.showJoinGameError('Failed to join game. Please try again.');
      }
    } finally {
      this.showLoading(false);
    }
  }

  private showJoinGameError(message: string) {
    this.elements.joinGameError.textContent = message;
    this.elements.joinGameError.style.display = 'block';
  }

  private async joinCurrentGame() {
    if (!this.gameState.game) {
      this.showError('No game loaded to join');
      return;
    }

    if (!this.gameState.isAuthenticated) {
      this.showError('Please login to join games');
      return;
    }

    this.showLoading(true);

    try {
      const game = await apiClient.joinGame(this.gameState.game.id);
      
      // Update local game state
      this.gameState.game = game;
      
      // Update the board
      if (game.currentBoard) {
        this.gameBoard.updateBoard(game.currentBoard);
        
        // Enable the board now that the user is a player
        this.gameBoard.setDisabled(false);
      }
      
      // Update UI
      this.updatePlayerInfo();
      this.updateGameControls();
      this.updateGameInfo();
      
      // Join SignalR group for this game
      await this.signalR.joinGameGroup(game.id);
      
      // Update status message
      if (game.players.length === 2) {
        const currentPlayer = game.currentBoard?.currentPlayerIndex === 0 ? 'White' : 'Black';
        this.updateGameStatus(`Game ready! ${currentPlayer} to move.`);
      } else {
        this.updateGameStatus(`Joined game! Waiting for another player...`);
      }
      
    } catch (error) {
      if (error instanceof ApiError) {
        this.showError(`Failed to join game: ${error.message}`);
      } else {
        this.showError('Failed to join game');
      }
    } finally {
      this.showLoading(false);
    }
  }


  private async logout() {
    // Leave current game group if any
    if (this.gameState.game) {
      await this.signalR.leaveGameGroup(this.gameState.game.id);
    }
    
    // Disconnect from SignalR
    await this.signalR.disconnect();
    
    // Logout via Keycloak (will redirect)
    await authService.logout();
  }

  private async loginAsGuest() {
    this.showLoading(true);
    
    try {
      const success = await authService.loginAsGuest();
      
      if (success) {
        this.gameState.isAuthenticated = true;
        this.gameState.currentUser = {
          id: authService.getUserId() || '',
          username: authService.getUsername() || 'Guest',
          rating: 1000,
          gamesPlayed: 0,
          gamesWon: 0,
          gamesLost: 0,
          gamesDrawn: 0
        };
        
        // Set the token in the API client
        const token = authService.getToken();
        if (token) {
          apiClient.setToken(token);
        }
        
        this.updateUserInfo();
        this.updateGameControls();
        
        // Connect to SignalR with guest token
        await this.connectSignalR();
        
        console.log('Logged in as guest');
      } else {
        this.showError('Failed to login as guest');
      }
    } catch (error) {
      console.error('Guest login error:', error);
      this.showError('Failed to login as guest');
    } finally {
      this.showLoading(false);
    }
  }

  private updateUserInfo() {
    const userInfo = this.elements.userInfo;
    console.log('Updating user info, authenticated:', this.gameState.isAuthenticated);
    
    if (this.gameState.isAuthenticated && this.gameState.currentUser) {
      const user = this.gameState.currentUser;
      const isGuest = authService.isGuest();
      userInfo.innerHTML = `
        <div class="user-details">
          <span class="username">${user.username}${isGuest ? ' (Guest)' : ''}</span>
          <span class="rating">Rating: ${user.rating}</span>
        </div>
        <button class="login-btn" id="logoutBtn">Logout</button>
      `;
      
      const logoutBtn = document.getElementById('logoutBtn')!;
      logoutBtn.addEventListener('click', () => this.logout());
    } else {
      console.log('Setting up login buttons...');
      userInfo.innerHTML = `
        <div style="display: flex; gap: 10px; align-items: center;">
          <button class="login-btn" id="loginBtn">Login</button>
          <button class="login-btn" id="guestBtn" style="background-color: #6c757d;">Play as Guest</button>
        </div>
      `;
      const loginBtn = document.getElementById('loginBtn')!;
      const guestBtn = document.getElementById('guestBtn')!;
      
      console.log('Login button found:', !!loginBtn);
      console.log('Guest button found:', !!guestBtn);
      
      loginBtn.addEventListener('click', () => authService.login());
      guestBtn.addEventListener('click', () => this.loginAsGuest());
    }
  }

  private updateUrlWithGameId(gameId: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('game', gameId);
    window.history.pushState({ gameId }, '', url.toString());
  }

  private getGameIdFromUrl(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('game');
  }

  private async loadGameFromUrl(gameId: string) {
    try {
      this.updateGameStatus('Loading game...');
      
      // Try to get the game details first (should work without authentication)
      const game = await apiClient.getGame(gameId);
      
      // Store the game
      this.gameState.game = game;
      
      // Determine user's relationship to the game
      const username = this.gameState.currentUser?.username;
      const isAuthenticated = this.gameState.isAuthenticated;
      const isPlayerInGame = username ? game.players.some(p => p.username === username) : false;
      const canJoin = isAuthenticated && !isPlayerInGame && game.players.length < 2;
      
      console.log('Game load debug:', {
        currentUsername: username,
        isAuthenticated,
        gamePlayers: game.players.map(p => ({ id: p.id, username: p.username })),
        isPlayerInGame,
        canJoin,
        boardWillBeDisabled: !isPlayerInGame
      });
      
      // Update the board and UI
      if (game.currentBoard) {
        this.gameBoard.updateBoard(game.currentBoard);
        
        // Only enable the board if the user is a player in the game
        this.gameBoard.setDisabled(!isPlayerInGame);
        
        // Record initial board state if game has moves
        if (game.recentMoves.length > 0) {
          this.recordBoardState(game.currentBoard);
        }
      }
      
      this.updatePlayerInfo();
      this.updateGameControls();
      this.updateGameInfo();
      this.clearMoveHistory();
      
      // Add recent moves to history if any
      if (game.recentMoves.length > 0) {
        game.recentMoves
          .sort((a, b) => a.moveNumber - b.moveNumber)
          .forEach(move => {
            const position = { row: move.fromX, col: move.fromY };
            this.addMoveToHistory(position, move.moveNumber);
          });
      }
      
      // Always join SignalR group to watch game updates live
      // Connect without token if not authenticated
      if (!this.signalR.isConnected()) {
        const token = authService.getToken();
        await this.signalR.connect(token || undefined);
      }
      await this.signalR.joinGameGroup(game.id);
      
      // If authenticated, not in game, and there's space, offer to join
      if (isAuthenticated) {
        if (canJoin) {
          this.updateGameStatus(`Game loaded! Click "Join Game" to participate.`);
          // Show join button or prompt
        } else if (isPlayerInGame) {
          // Player is in the game
          if (game.players.length === 2) {
            const currentPlayer = game.currentBoard?.currentPlayerIndex === 0 ? 'White' : 'Black';
            this.updateGameStatus(`Game loaded! ${currentPlayer} to move.`);
          } else {
            this.updateGameStatus(`Game loaded! Waiting for another player...`);
          }
        } else if (isAuthenticated) {
          // Authenticated but game is full
          this.updateGameStatus('Viewing game as spectator (game is full)');
        }
      } else {
        // Not authenticated - view only mode
        if (game.status === 'Completed') {
          const winner = game.winnerId ? game.players.find(p => p.id === game.winnerId)?.username : null;
          const winnerText = winner ? `${winner} wins!` : 'Draw!';
          this.updateGameStatus(`Viewing completed game - ${winnerText}`);
        } else {
          this.updateGameStatus('Viewing game (login to play)');
        }
      }
      
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.status === 404) {
          this.showError('Game not found. The game ID in the URL may be invalid.');
          // Clear the invalid game ID from URL
          const url = new URL(window.location.href);
          url.searchParams.delete('game');
          window.history.replaceState({}, '', url.toString());
        } else if (error.status === 401) {
          // This shouldn't happen anymore since we're allowing anonymous viewing
          this.showError('Authentication required to view this game.');
        } else {
          this.showError(`Failed to load game: ${error.message}`);
        }
      } else {
        this.showError('Failed to load game from URL');
      }
    }
  }

  private async startNewGame() {
    if (!this.gameState.isAuthenticated || !this.gameState.currentUser) {
      this.showError('Please login to start a game. Redirecting to login...');
      setTimeout(() => authService.login(), 1500);
      return;
    }

    this.showLoading(true);
    this.updateGameStatus('Creating new game...');
    
    try {
      const gameData: CreateGameDto = {
        maxMoves: 200,
        playerIds: [] // Players join separately for consistency
      };
      
      const createdGame = await apiClient.createGame(gameData);
      
      // Now join the game to ensure consistent player setup
      const game = await apiClient.joinGame(createdGame.id);
      this.gameState.game = game;
      
      // Update URL with game ID
      this.updateUrlWithGameId(game.id);
      
      if (game.currentBoard) {
        this.gameBoard.updateBoard(game.currentBoard);
      }
      
      this.updatePlayerInfo();
      this.updateGameControls();
      this.updateGameInfo();
      this.clearMoveHistory();
      
      // Join SignalR group for this game
      await this.signalR.joinGameGroup(game.id);
      
      this.updateGameStatus('Game started! Share the Game ID with someone to play together.');
      
    } catch (error) {
      if (error instanceof ApiError) {
        this.showError(`Failed to create game: ${error.message}`);
      } else {
        this.showError('Failed to create game');
      }
      this.updateGameStatus('Failed to start game');
    } finally {
      this.showLoading(false);
    }
  }

  private async handleCellClick(position: Position) {
    if (!this.gameState.game) {
      return;
    }
    
    // Don't allow moves during playback
    if (this.playbackState.isPlayback) {
      this.showError('Return to live view to make moves');
      return;
    }
    
    // Prevent double-clicks by temporarily disabling while processing
    if (this.gameState.isMakingMove) {
      return;
    }

    // Check if it's a valid move
    const board = this.gameState.game.currentBoard;
    if (!board) return;
    
    // Check if it's the current player's turn
    if (this.gameState.currentUser && this.gameState.game.players.length > board.currentPlayerIndex) {
      const currentPlayer = this.gameState.game.players[board.currentPlayerIndex];
      if (currentPlayer.username !== this.gameState.currentUser.username) {
        // Not your turn - show subtle feedback instead of error
        this.gameBoard.showInvalidMoveAnimation(position);
        this.updateGameStatus(`Waiting for ${currentPlayer.username}'s move...`);
        return;
      }
    }
    
    const validMove = board.validMoves.find(move => 
      move[0] === position.row && move[1] === position.col
    );
    
    if (!validMove) {
      this.gameBoard.showInvalidMoveAnimation(position);
      return;
    }

    // Set flag to prevent double-clicks
    this.gameState.isMakingMove = true;
    this.updateGameStatus('Making move...');
    
    try {
      const moveData: MakeMoveDto = {
        fromX: position.row,
        fromY: position.col,
        toX: position.row,
        toY: position.col
      };
      
      console.log('Making move:', moveData, 'for game:', this.gameState.game.id);
      const result = await apiClient.makeMove(this.gameState.game.id, moveData);
      console.log('Move result:', result);
      
      if (result.success && result.newBoardState) {
        this.gameBoard.updateBoard(result.newBoardState);
        this.gameBoard.highlightLastMove(position);
        this.gameBoard.animateMove(position);
        
        // Update game state
        if (this.gameState.game.currentBoard) {
          this.gameState.game.currentBoard = result.newBoardState;
          this.gameState.game.totalMoves++;
        }
        
        // Record board state for playback
        this.recordBoardState(result.newBoardState);
        
        this.updatePlayerInfo();
        this.addMoveToHistory(position, result.newBoardState.moveCount);
        this.updateGameStatistics();
        
        if (result.gameEnded) {
          if (result.winnerId) {
            const winner = this.gameState.game.players.find(p => p.id === result.winnerId);
            this.updateGameStatus(`Game over! ${winner ? winner.username : 'Player'} wins!`);
          } else {
            this.updateGameStatus('Game over! Draw!');
          }
          this.gameBoard.setDisabled(true);
        } else {
          const currentPlayer = result.newBoardState.currentPlayerIndex === 0 ? 'White' : 'Black';
          this.updateGameStatus(`${currentPlayer} to move`);
        }
      } else {
        this.showError(result.error || 'Move failed');
      }
      
    } catch (error) {
      if (error instanceof ApiError) {
        this.showError(`Move failed: ${error.message}`);
      } else {
        this.showError('Move failed');
      }
    } finally {
      // Always reset the flag
      this.gameState.isMakingMove = false;
    }
  }

  private async resignGame() {
    if (!this.gameState.game || !this.gameState.isAuthenticated) {
      return;
    }

    if (!confirm('Are you sure you want to resign?')) {
      return;
    }

    this.showLoading(true);
    
    try {
      const game = await apiClient.resignGame(this.gameState.game.id);
      this.gameState.game = game;
      this.updateGameStatus('Game resigned');
      this.gameBoard.setDisabled(true);
      
    } catch (error) {
      if (error instanceof ApiError) {
        this.showError(`Resign failed: ${error.message}`);
      } else {
        this.showError('Resign failed');
      }
    } finally {
      this.showLoading(false);
    }
  }

  private updatePlayerInfo() {
    const game = this.gameState.game;
    if (!game) return;

    // Update player names - this should always happen if we have players
    if (game.players.length >= 1) {
      const player1Name = this.elements.player1Info.querySelector('.player-name')!;
      player1Name.textContent = game.players[0].username;
    }
    
    if (game.players.length >= 2) {
      const player2Name = this.elements.player2Info.querySelector('.player-name')!;
      player2Name.textContent = game.players[1].username;
    } else {
      // Reset Player 2 name if there's no second player
      const player2Name = this.elements.player2Info.querySelector('.player-name')!;
      player2Name.textContent = 'Waiting for player...';
    }

    // Only update turn indicators if we have a board
    if (game.currentBoard) {
      const currentPlayerIndex = game.currentBoard.currentPlayerIndex;
      
      // Update turn indicators
      this.elements.player1Turn.classList.toggle('turn-indicator--active', currentPlayerIndex === 0);
      this.elements.player2Turn.classList.toggle('turn-indicator--active', currentPlayerIndex === 1);
      
      // Update player info bars
      this.elements.player1Info.classList.toggle('player-info--active', currentPlayerIndex === 0);
      this.elements.player2Info.classList.toggle('player-info--active', currentPlayerIndex === 1);
    }
  }

  private updateGameControls() {
    const hasGame = !!this.gameState.game;
    const isAuthenticated = this.gameState.isAuthenticated;
    const username = this.gameState.currentUser?.username;
    const isPlayerInGame = hasGame && username ? 
      this.gameState.game!.players.some(p => p.username === username) : false;
    const gameHasSpace = hasGame && this.gameState.game!.players.length < 2;
    const gameInProgress = hasGame && this.gameState.game?.status === 'InProgress';
    
    // Show/hide buttons based on context
    // "Join By ID" - always show if authenticated and not in a game
    this.elements.joinByIdBtn.style.display = 
      isAuthenticated && (!hasGame || !isPlayerInGame) ? 'inline-block' : 'none';
    
    // "Join This Game" - show if viewing a game with space and not already in it
    this.elements.joinThisGameBtn.style.display = 
      isAuthenticated && hasGame && gameHasSpace && !isPlayerInGame ? 'inline-block' : 'none';
    
    // "Resign" - only show if actively playing in a game in progress
    this.elements.resignBtn.style.display = 
      isPlayerInGame && gameInProgress ? 'inline-block' : 'none';
    
    // Disable resign button if game is not in progress
    (this.elements.resignBtn as HTMLButtonElement).disabled = !gameInProgress;
  }

  private updateGameStatus(message: string) {
    const statusText = this.elements.gameStatus.querySelector('.status-text')!;
    statusText.textContent = message;
  }

  private addMoveToHistory(position: Position, moveNumber: number) {
    const notation = this.gameBoard.getAlgebraicFromPosition(position);
    const moveEntry = document.createElement('div');
    moveEntry.className = 'move-entry';
    moveEntry.textContent = `${moveNumber}. ${notation}`;
    
    // Make the entry clickable to jump to that move
    const moveIndex = this.playbackState.boardHistory.length - 1;
    moveEntry.addEventListener('click', () => {
      this.goToMove(moveIndex);
    });
    
    this.elements.moveHistory.appendChild(moveEntry);
    this.elements.moveHistory.scrollTop = this.elements.moveHistory.scrollHeight;
    
    // Update playback controls since we have a new move
    if (this.playbackState.currentIndex === -1) {
      this.updatePlaybackControls();
    }
  }

  private setTheme(theme: Theme) {
    this.uiState.theme = theme;
    document.documentElement.setAttribute('data-theme', theme);
    this.elements.themeSelect.value = theme;
    localStorage.setItem('theme', theme);
  }

  private updateGameInfo() {
    const hasGame = !!this.gameState.game;
    this.elements.gameInfo.style.display = hasGame ? 'block' : 'none';
    this.elements.gameStats.style.display = hasGame ? 'block' : 'none';
    
    if (hasGame && this.gameState.game) {
      this.elements.gameIdDisplay.value = this.gameState.game.id;
      this.updateGameStatistics();
    }
  }

  private updateGameStatistics() {
    if (!this.gameState.game || !this.gameState.game.currentBoard) {
      return;
    }

    const board = this.gameState.game.currentBoard;
    let whiteYugos = 0;
    let blackYugos = 0;
    let whiteMigos = 0;
    let blackMigos = 0;

    // Count pieces on the board
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const cell = board.cells[row][col];
        if (cell.type === 1) { // Migo
          if (cell.playerIndex === 0) {
            whiteMigos++;
          } else if (cell.playerIndex === 1) {
            blackMigos++;
          }
        } else if (cell.type === 3) { // Yugo (corrected from 2 to 3)
          if (cell.playerIndex === 0) {
            whiteYugos++;
          } else if (cell.playerIndex === 1) {
            blackYugos++;
          }
        }
        // Note: cell.type === 2 is Nexus, which we don't count
      }
    }

    // Update the display
    this.elements.whiteYugos.textContent = whiteYugos.toString();
    this.elements.blackYugos.textContent = blackYugos.toString();
    this.elements.whiteMigos.textContent = whiteMigos.toString();
    this.elements.blackMigos.textContent = blackMigos.toString();
  }

  private async copyGameId() {
    if (!this.gameState.game) return;
    
    try {
      await navigator.clipboard.writeText(this.gameState.game.id);
      
      // Visual feedback
      const originalText = this.elements.copyGameIdBtn.textContent;
      this.elements.copyGameIdBtn.textContent = '✓';
      this.elements.copyGameIdBtn.style.background = '#27ae60';
      
      setTimeout(() => {
        this.elements.copyGameIdBtn.textContent = originalText;
        this.elements.copyGameIdBtn.style.background = '';
      }, 1500);
      
    } catch (error) {
      // Fallback for older browsers
      this.elements.gameIdDisplay.select();
      document.execCommand('copy');
      this.showError('Game ID copied to clipboard!');
    }
  }

  private clearMoveHistory() {
    this.elements.moveHistory.innerHTML = '';
    this.clearHistory();
  }

  // SignalR Event Handlers
  private handleRemoteMove(gameId: string, move: Position | undefined, result: any) {
    // Only handle moves for the current game
    if (!this.gameState.game || this.gameState.game.id !== gameId) {
      return;
    }

    // Skip if this is our own move (we already updated the board when we made the move)
    if (this.gameState.currentUser && this.gameState.game) {
      const player = this.gameState.game.players.find(p => p.id === result.playerId);
      if (player && player.username === this.gameState.currentUser.username) {
        console.log('Skipping own move notification');
        return;
      }
    }

    console.log('Received remote move:', move, result);

    // Update the board with the new move
    if (result.boardState) {
      this.gameBoard.updateBoard(result.boardState);
      if (move) {
        this.gameBoard.highlightLastMove(move);
        this.gameBoard.animateMove(move);
      }
      
      // Update game state
      if (this.gameState.game.currentBoard) {
        this.gameState.game.currentBoard = result.boardState;
        this.gameState.game.totalMoves++;
      }
      
      // Record board state for playback
      this.recordBoardState(result.boardState);
      
      // Reset to live view when opponent makes a move
      if (this.playbackState.isPlayback) {
        this.goToLive();
      }
      
      this.updatePlayerInfo();
      if (move) {
        this.addMoveToHistory(move, result.boardState.moveCount || this.gameState.game.totalMoves);
      }
      this.updateGameStatistics();
      
      if (result.gameEnded) {
        if (result.winnerId) {
          const winner = this.gameState.game.players.find((p: any) => p.id === result.winnerId);
          this.updateGameStatus(`Game over! ${winner ? winner.username : 'Player'} wins!`);
        } else {
          this.updateGameStatus('Game over! Draw!');
        }
        this.gameBoard.setDisabled(true);
      } else {
        const currentPlayer = result.boardState.currentPlayerIndex === 0 ? 'White' : 'Black';
        this.updateGameStatus(`${currentPlayer} to move`);
      }
    }
  }

  private handlePlayerJoined(gameId: string, player: any) {
    if (!this.gameState.game || this.gameState.game.id !== gameId) {
      return;
    }

    console.log('Player joined:', player);
    
    // Add player to game if not already there
    if (!this.gameState.game.players.some(p => p.id === player.id)) {
      this.gameState.game.players.push(player);
    }
    
    this.updatePlayerInfo();
    this.updateGameStatus(`${player.username} joined the game!`);
  }

  private handlePlayerLeft(gameId: string, playerId: string) {
    if (!this.gameState.game || this.gameState.game.id !== gameId) {
      return;
    }

    console.log('Player left:', playerId);
    
    // Remove player from game
    const playerIndex = this.gameState.game.players.findIndex(p => p.id === playerId);
    if (playerIndex !== -1) {
      const player = this.gameState.game.players[playerIndex];
      this.gameState.game.players.splice(playerIndex, 1);
      this.updatePlayerInfo();
      this.updateGameStatus(`${player.username} left the game`);
    }
  }

  private handleGameStateChanged(gameId: string, game: any) {
    if (!this.gameState.game || this.gameState.game.id !== gameId) {
      return;
    }

    console.log('Game state changed:', game);
    
    // Update local game state
    this.gameState.game = game;
    
    if (game.currentBoard) {
      this.gameBoard.updateBoard(game.currentBoard);
    }
    
    this.updatePlayerInfo();
    this.updateGameControls();
  }

  private handleConnectionChanged(connected: boolean) {
    this.updateConnectionStatus(connected);
    
    if (connected) {
      // Rejoin game group if we have an active game
      if (this.gameState.game) {
        this.signalR.joinGameGroup(this.gameState.game.id);
      }
    } else {
      this.updateGameStatus('Connection lost - attempting to reconnect...');
    }
  }

  private showError(message: string) {
    // Show error in the game status area instead of alert
    const statusText = document.querySelector('.status-text');
    if (statusText) {
      const originalText = statusText.textContent;
      statusText.textContent = `⚠️ ${message}`;
      statusText.classList.add('error-message');
      
      // Restore original text after 3 seconds
      setTimeout(() => {
        statusText.classList.remove('error-message');
        if (statusText.textContent?.startsWith('⚠️')) {
          statusText.textContent = originalText;
        }
      }, 3000);
    }
  }

  // Playback Methods
  private goToMove(index: number) {
    if (index < 0 || index >= this.playbackState.boardHistory.length) {
      return;
    }
    
    this.playbackState.currentIndex = index;
    this.playbackState.isPlayback = true;
    
    const boardState = this.playbackState.boardHistory[index];
    this.gameBoard.updateBoard(boardState);
    
    this.updatePlaybackUI();
    this.updatePlaybackControls();
  }
  
  private goToPreviousMove() {
    if (this.playbackState.currentIndex === -1) {
      // Currently live, go to second-to-last move (last move has same state as live)
      const targetIndex = Math.max(0, this.playbackState.boardHistory.length - 2);
      this.goToMove(targetIndex);
    } else if (this.playbackState.currentIndex > 0) {
      this.goToMove(this.playbackState.currentIndex - 1);
    }
  }
  
  private goToNextMove() {
    if (this.playbackState.currentIndex < this.playbackState.boardHistory.length - 1) {
      this.goToMove(this.playbackState.currentIndex + 1);
    } else if (this.playbackState.currentIndex === this.playbackState.boardHistory.length - 1) {
      // At last move, return to live
      this.returnToLive();
    }
  }
  
  private goToLastMove() {
    const lastIndex = this.playbackState.boardHistory.length - 1;
    if (lastIndex >= 0) {
      this.goToMove(lastIndex);
    }
  }
  
  private returnToLive() {
    this.playbackState.currentIndex = -1;
    this.playbackState.isPlayback = false;
    
    // Restore current board state
    if (this.gameState.game?.currentBoard) {
      this.gameBoard.updateBoard(this.gameState.game.currentBoard);
    }
    
    this.updatePlaybackUI();
    this.updatePlaybackControls();
  }
  
  // Alias for returnToLive for backwards compatibility
  private goToLive() {
    this.returnToLive();
  }
  
  private updatePlaybackUI() {
    const gameBoard = this.elements.gameBoard;
    const liveBtn = this.elements.liveBtn;
    const positionDiv = this.elements.playbackPosition;
    
    if (this.playbackState.isPlayback) {
      gameBoard.classList.add('game-board--playback');
      liveBtn.classList.add('inactive');
      positionDiv.classList.add('playback-position--history');
      
      const moveNum = this.playbackState.currentIndex + 1;
      const totalMoves = this.playbackState.boardHistory.length;
      this.elements.currentMoveText.textContent = `Move ${moveNum} of ${totalMoves}`;
    } else {
      gameBoard.classList.remove('game-board--playback');
      liveBtn.classList.remove('inactive');
      positionDiv.classList.remove('playback-position--history');
      this.elements.currentMoveText.textContent = 'Live View';
    }
    
    // Update move history highlighting
    const moveEntries = this.elements.moveHistory.querySelectorAll('.move-entry');
    moveEntries.forEach((entry, index) => {
      if (index === this.playbackState.currentIndex) {
        entry.classList.add('move-entry--current');
      } else {
        entry.classList.remove('move-entry--current');
      }
    });
  }
  
  private updatePlaybackControls() {
    const { currentIndex, boardHistory } = this.playbackState;
    const hasHistory = boardHistory.length > 0;
    
    this.elements.firstMoveBtn.disabled = !hasHistory || currentIndex === 0;
    this.elements.prevMoveBtn.disabled = !hasHistory || currentIndex === 0;
    this.elements.nextMoveBtn.disabled = !hasHistory || currentIndex === -1;
    this.elements.lastMoveBtn.disabled = !hasHistory || currentIndex === boardHistory.length - 1;
    this.elements.liveBtn.disabled = currentIndex === -1;
  }
  
  private recordBoardState(boardState: BoardStateDto) {
    this.playbackState.boardHistory.push(JSON.parse(JSON.stringify(boardState)));
    
    // If we're in live view, update controls
    if (this.playbackState.currentIndex === -1) {
      this.updatePlaybackControls();
    }
  }
  
  private clearHistory() {
    this.playbackState.boardHistory = [];
    this.playbackState.moveHistory = [];
    this.playbackState.currentIndex = -1;
    this.playbackState.isPlayback = false;
    this.updatePlaybackUI();
    this.updatePlaybackControls();
  }
}

// Initialize the app when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new MigoYugoApp();
});